import React, { useCallback, useEffect, useState } from "react";
import { Workshop } from "../types";
import { workshopService } from "../services/dataService";
import { poErr } from "../utils/purchasing";
import { SinglePicker, PickColumn, trLower, dash } from "./PickerModal";
import "./ModelModal.css"; // eski genel kurallar başka ekranlarda kullanılıyor; bkz. ModelModal.tsx

interface WorkshopModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (workshop: Workshop) => void;
}

const emptyWorkshop = { name: "", description: "", location: "", contactPerson: "", phone: "" };

const columns: PickColumn<Workshop>[] = [
  {
    head: "Atölye",
    cell: (w) => (
      <>
        <span className="po-name">{w.name}</span>
        {w.description && <div className="po-sub">{w.description}</div>}
        {(w.location || w.phone) && <div className="po-sub po-only-sm">{[w.location, w.phone].filter(Boolean).join(" · ")}</div>}
      </>
    ),
  },
  { head: "Konum", className: "po-hide-sm", has: (w) => !!w.location, cell: (w) => w.location || dash },
  { head: "Yetkili", className: "po-hide-md", has: (w) => !!w.contactPerson, cell: (w) => w.contactPerson || dash },
  { head: "Telefon", className: "po-hide-sm", has: (w) => !!w.phone, cell: (w) => w.phone || dash },
];

const match = (w: Workshop, q: string) =>
  trLower(w.name).includes(q) || trLower(w.location).includes(q) || trLower(w.contactPerson).includes(q) || trLower(w.description).includes(q) || (w.phone || "").includes(q);

const WorkshopPicker: React.FC<Omit<WorkshopModalProps, "isOpen">> = ({ onClose, onSelect }) => {
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [newWorkshop, setNewWorkshop] = useState(emptyWorkshop);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadWorkshops = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setWorkshops(await workshopService.getAll());
    } catch (err) {
      console.error("Atölyeler yüklenemedi:", err);
      setLoadError("Atölyeler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWorkshops(); }, [loadWorkshops]);

  const handleAddWorkshop = async () => {
    setSaving(true);
    setError("");
    try {
      const workshopData: any = {
        name: newWorkshop.name.trim(),
        description: newWorkshop.description || undefined,
        location: newWorkshop.location || undefined,
        contactPerson: newWorkshop.contactPerson || undefined,
        phone: newWorkshop.phone || undefined,
      };
      const created = await workshopService.create(workshopData);
      setNewWorkshop(emptyWorkshop);
      onSelect(created);
      onClose();
    } catch (err: any) {
      setError("Atölye eklenemedi: " + poErr(err, err?.message || "bilinmeyen hata"));
      setSaving(false);
    }
  };

  const set = (k: keyof typeof emptyWorkshop, v: string) => { setNewWorkshop((p) => ({ ...p, [k]: v })); setError(""); };

  return (
    <SinglePicker<Workshop>
      kicker="Atölye"
      title="Atölye seç"
      noun="atölye"
      items={workshops}
      loading={loading}
      loadError={loadError}
      onRetry={loadWorkshops}
      rowKey={(w) => w.workshopId}
      rowLabel={(w) => w.name}
      match={match}
      placeholder="Atölye adı, konum, yetkili veya telefon ara"
      columns={columns}
      emptyText="İlk atölyeyi buradan ekleyebilirsiniz."
      onPick={(w) => { onSelect(w); onClose(); }}
      onClose={onClose}
      create={{
        label: "Yeni atölye",
        title: "Yeni atölye",
        hint: "Ayrıntılar sonra Atölyeler ekranından düzenlenebilir.",
        onOpen: (q) => { if (q && !newWorkshop.name) set("name", q); },
        onSubmit: handleAddWorkshop,
        saving,
        error,
        valid: !!newWorkshop.name.trim(),
        fields: (
          <>
            <div className="po-grid2">
              <label className="po-field">
                <span>Atölye adı<em>zorunlu</em></span>
                <input value={newWorkshop.name} required onChange={(e) => set("name", e.target.value)} placeholder="Örn. Baskı atölyesi" />
              </label>
              <label className="po-field">
                <span>Konum</span>
                <input value={newWorkshop.location} onChange={(e) => set("location", e.target.value)} />
              </label>
              <label className="po-field">
                <span>İletişim kişisi</span>
                <input value={newWorkshop.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} />
              </label>
              <label className="po-field">
                <span>Telefon</span>
                <input type="tel" value={newWorkshop.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0212 000 00 00" />
              </label>
            </div>
            <label className="po-field">
              <span>Açıklama</span>
              <textarea rows={3} value={newWorkshop.description} onChange={(e) => set("description", e.target.value)} />
            </label>
          </>
        ),
      }}
    />
  );
};

/** Atölye seçici: arama, klavye ile seçim ve satır içi yeni atölye formu. */
const WorkshopModal: React.FC<WorkshopModalProps> = ({ isOpen, ...rest }) => (isOpen ? <WorkshopPicker {...rest} /> : null);

export default WorkshopModal;
